import { useState } from 'react';
import * as React from 'react';
import cn from 'classnames';
import { MenuOutlined } from '@ant-design/icons';
import { Drawer } from 'antd';
import { useTranslation } from 'react-i18next';
import useAnalytics from '@shared/useAnalytics';
import { PARAMETERS } from '@root/tracking/constants';
import { useLocation } from 'react-router-dom';
import { CurrentUser } from '@root/types';
import useEnv from '@shared/useEnv';
import routes from '@root/routes';
import CustomIcon from '@components/shared/CustomIcon';
import Logo from '@components/layouts/Truva/Layout/Navigation/Logo';
import s from './MobileNavigation.module.less';
import { TrackerProps } from '../Navigation';
import UniversalSearch from '../UniversalSearch';

type SearchIconProps = {
  onClick: (e: React.SyntheticEvent<HTMLButtonElement>) => void;
};

const SearchIcon = ({ onClick }: SearchIconProps) => (
  <div className={s.searchIcon}>
    <button onClick={onClick} type="button" title="Toggle Search">
      <CustomIcon title="Search Icon" type="magnifying-glass" className={s.magnifying} />
    </button>
  </div>
);

type MobileNavigationProps = {
  showUniversalSearch: boolean;
  currentUser: CurrentUser | null;
  trackers: TrackerProps;
  hideLogInAndSignUp: boolean;
};

const MobileNavigation = ({
  showUniversalSearch,
  currentUser,
  trackers,
  hideLogInAndSignUp = false,
}: MobileNavigationProps) => {
  const [showSearch, setShowSearch] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const { t } = useTranslation('navigation');
  const location = useLocation();
  const { vtsUrl } = useEnv();
  const { clickToPage, anonymousId } = useAnalytics();

  const toggleShowSearch = () => {
    setShowSearch(prevShowSearch => !prevShowSearch);
  };

  const userSpecificNavigation = () => {
    if (currentUser) {
      return (
        <>
          <div className={s.highlight}>
            <a
              href={routes.listingSearch()}
              className={s.navigationHeader}
              onClick={trackers.searchClicked}
            >
              <CustomIcon type="magnifying-glass" className={s.heart} />
              {t('search')}
            </a>
          </div>
          <div className={s.highlight}>
            <a
              href={routes.tourbooks}
              className={s.navigationHeader}
              onClick={trackers.tourbooksClicked}
            >
              <CustomIcon type="tourbook" className={s.heart} />
              {t('tourbook')}
            </a>
          </div>
          <div className={s.highlight}>
            <span className={s.navigationHeader}>
              <CustomIcon type="heart-outline" className={s.heart} />
              {t('favorites')}
            </span>
          </div>
          <div className={s.highlight}>
            <a href={routes.savedListings} className={s.navigationLink}>
              {t('listings')}
            </a>
          </div>
          <div className={s.highlight}>
            <a href={routes.savedSearches} className={s.navigationLink}>
              {t('searches')}
            </a>
          </div>
          <div className={s.highlight}>
            <a href={routes.savedBuildings} className={s.navigationLink}>
              {t('buildings')}
            </a>
          </div>
          <div className={s.highlight}>
            <a href={routes.savedBuildingOwners} className={s.navigationLink}>
              {t('buildingOwners')}
            </a>
          </div>
          <div className={s.user}>
            <span className={s.navigationHeader}>
              <CustomIcon type="person-circle" className={s.heart} />
              {currentUser.firstName ? currentUser.firstName : currentUser.email}
            </span>
          </div>
          <div className={s.highlight}>
            <a
              href={routes.users.edit('professional_info')}
              className={s.navigationLink}
              onClick={trackers.accountClicked}
            >
              {t('account')}
            </a>
          </div>
          <div className={s.highlight}>
            <a href="/logout" className={s.navigationLink}>
              {t('signOut')}
            </a>
          </div>
          <div className={s.highlight}>
            <a href="https://app.vts.com" className={s.vtsHeader}>
              {t('vtsHome')}
            </a>
          </div>
        </>
      );
    }

    return (
      <>
        <div className={s.highlight}>
          <a
            href={routes.signUp(vtsUrl!, {
              utm_content: 'marketplace nav',
              anonymous_id: anonymousId,
            })}
            className={s.navigationLink}
            onClick={() => {
              clickToPage({
                destination: PARAMETERS.signUpPage,
                sourceContent: PARAMETERS.navigation,
              });
            }}
            key="sign-up-link"
          >
            {t('signUp')}
          </a>
        </div>
        <div className={s.highlight}>
          <a href="/login" className={s.navigationLink} key="sign-in-link">
            {t('logIn')}
          </a>
        </div>
      </>
    );
  };

  return (
    <div className={s.container}>
      {!currentUser && hideLogInAndSignUp ? null : (
        <button className={s.menuIcon} onClick={() => setShowMenu(true)} type="button">
          <MenuOutlined title="Navigation menu" />
        </button>
      )}
      <Logo />
      {showUniversalSearch && <SearchIcon onClick={toggleShowSearch} />}
      <Drawer
        className={s.menuDrawer}
        placement="left"
        visible={showMenu}
        onClose={() => setShowMenu(false)}
      >
        {userSpecificNavigation()}
      </Drawer>
      <div
        data-testid="universalSearchContainer"
        className={cn(s.mobileSearch, (!showUniversalSearch || !showSearch) && s.hide)}
      >
        {showUniversalSearch && (
          <UniversalSearch
            handleSelect={toggleShowSearch}
            visible={showSearch}
            location={location}
          />
        )}
        {showSearch && showUniversalSearch && (
          <div className={s.hackyOverlay} onClick={toggleShowSearch} role="button" />
        )}
      </div>
    </div>
  );
};

export default MobileNavigation;
